import { Link } from "@tanstack/react-router";
import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  groupSidebarNavItems,
  isSidebarNavItemActive,
  type SidebarNavItem,
} from "@/components/ds/sidebar-nav-presentation";

export function SidebarNav({
  items,
  pathname,
  onNavigate,
  collapsed = false,
}: {
  items: SidebarNavItem[];
  pathname: string;
  onNavigate?: () => void;
  /** SCRUM-252: modo ícono-solo del sidebar desktop colapsado. */
  collapsed?: boolean;
}) {
  const groups = groupSidebarNavItems(items);

  return (
    <TooltipProvider delayDuration={200}>
      <nav
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-0.5"
        data-testid="merchant-sidebar-nav"
        data-sidebar-collapsed={collapsed ? "true" : "false"}
        aria-label="Módulos"
      >
        {groups.map((group) => (
          <section key={group.id} className="mb-2 last:mb-0.5" aria-labelledby={`sidebar-group-${group.id}`}>
            {!collapsed ? (
              <h2
                id={`sidebar-group-${group.id}`}
                className="px-2 pb-1 text-[10px] font-medium leading-none uppercase tracking-[0.14em] text-muted-foreground"
              >
                {group.label}
              </h2>
            ) : null}
            <div className="flex flex-col">
              {group.items.map((item) => {
                const isActive = isSidebarNavItemActive(pathname, item.to);
                const tourKey = item.to.replace("/app/", "nav-").replace("/", "-");
                const locked = item.gateState === "locked_feature";
                const tooltipLabel = locked && item.minPlanLabel
                  ? `${item.label} — Disponible en ${item.minPlanLabel}`
                  : item.label;

                const link = (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-tour={tourKey}
                    data-nav-to={item.to}
                    aria-current={isActive ? "page" : undefined}
                    aria-label={collapsed ? tooltipLabel : undefined}
                    onClick={() => onNavigate?.()}
                    title={!collapsed && locked && item.minPlanLabel ? `Disponible en ${item.minPlanLabel}` : undefined}
                    className={cn(
                      "flex h-8 items-center gap-2 rounded-md px-2 text-[13px] font-medium leading-none transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus-visible:ring-offset-sidebar",
                      collapsed && "justify-center px-0",
                      isActive
                        ? "bg-primary/10 font-semibold text-sidebar-primary"
                        : "bg-transparent text-sidebar-foreground hover:bg-surface-hover hover:text-foreground",
                    )}
                  >
                    <item.icon
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isActive ? "text-sidebar-primary" : "text-muted-foreground",
                      )}
                      aria-hidden
                    />
                    {!collapsed ? (
                      <>
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {locked ? <Lock className="h-3 w-3 shrink-0 text-primary" aria-hidden /> : null}
                      </>
                    ) : null}
                  </Link>
                );

                if (!collapsed) return link;

                return (
                  <Tooltip key={item.to}>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right">{tooltipLabel}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </section>
        ))}
      </nav>
    </TooltipProvider>
  );
}
